import { z } from "zod";
import { passwordSchema, roleSchema } from "./auth.js";
const count = z.number().int().nonnegative();
const page = z.coerce.number().int().positive().default(1);
const perPage = z.coerce.number().int().positive().max(100).default(20);
// ---------------------------------------------------------------------------
// Overview — /admin/overview
// ---------------------------------------------------------------------------
/** A total, and how much of it came in the last 30 days. */
export const adminKpiSchema = z.object({
    total: count,
    current: count,
});
export const ADMIN_LIBRARY_STATUSES = [
    "WATCHING",
    "PLANNED",
    "COMPLETED",
    "ON_HOLD",
    "DROPPED",
];
/** One audience's activity over one window. */
export const adminAudienceStatsSchema = z.object({
    /** Distinct browsers (the random visitor id) that opened any page. */
    visitors: count,
    pageviews: count,
    /** Pageviews of a title page — /anime/… */
    animeViews: count,
    /** Distinct titles those views landed on. */
    titles: count,
    watchSessions: count,
    watchSeconds: count,
});
export const ADMIN_WINDOWS = ["today", "7d", "30d"];
/** The overview's top: the numbers and the registered/guests split —
 *  /admin/overview. The rest of the overview loads block by block. */
export const adminOverviewSchema = z.object({
    /** Signed-in visitors next to guests, for each window — the two columns
     *  the dashboard is built around. */
    split: z.array(z.object({
        window: z.enum(ADMIN_WINDOWS),
        registered: adminAudienceStatsSchema,
        guests: adminAudienceStatsSchema,
    })),
    kpis: z.object({
        users: adminKpiSchema,
        /** `total`: every browser ever seen; `current`: the last 30 days. */
        visitors: adminKpiSchema,
        pageviews: adminKpiSchema,
        comments: adminKpiSchema,
    }),
});
// ---------------------------------------------------------------------------
// Users — /admin/users
// ---------------------------------------------------------------------------
export const adminUserQuerySchema = z.object({
    query: z.string().trim().max(200).optional(),
    role: z.enum(["all", "USER", "ADMIN"]).default("all"),
    status: z.enum(["all", "active", "banned"]).default("all"),
    sort: z.enum(["newest", "oldest", "lastSeen", "name", "comments", "library"]).default("newest"),
    page,
    perPage,
});
export const adminUserSummarySchema = z.object({
    id: z.string(),
    email: z.string(),
    displayName: z.string(),
    avatarUrl: z.string().nullable(),
    role: roleSchema,
    isBanned: z.boolean(),
    hasTelegram: z.boolean(),
    username: z.string().nullable(),
    isPro: z.boolean(),
    commentCount: count,
    libraryCount: count,
    /** Seconds watched in the player, all time. */
    watchSeconds: count,
    /** Last page opened while signed in; null if never seen since tracking began. */
    lastSeenAt: z.string().nullable(),
    createdAt: z.string(),
});
export const adminUpdateUserInputSchema = z
    .object({
    role: roleSchema.optional(),
    isBanned: z.boolean().optional(),
    /** Experience granted on top of what the account earned (sets the level). */
    bonusXp: z.number().int().min(0).max(2_000_000_000).optional(),
})
    .refine((v) => v.role !== undefined || v.isBanned !== undefined || v.bonusXp !== undefined, {
    message: "Nothing to update",
});
// ---------------------------------------------------------------------------
// Comments — /admin/comments
// ---------------------------------------------------------------------------
export const adminCommentQuerySchema = z.object({
    query: z.string().trim().max(200).optional(),
    animeId: z.coerce.number().int().positive().optional(),
    status: z.enum(["all", "active", "deleted", "reported"]).default("all"),
    sort: z.enum(["newest", "oldest", "top"]).default("newest"),
    page,
    perPage,
});
export const adminCommentSummarySchema = z.object({
    id: z.string(),
    animeId: z.number().int(),
    animeSlug: z.string(),
    animeTitle: z.string(),
    animeImageUrl: z.string().nullable(),
    authorId: z.string(),
    authorName: z.string(),
    authorAvatarUrl: z.string().nullable(),
    body: z.string(),
    mode: z.string(),
    likeCount: count,
    dislikeCount: count,
    deleted: z.boolean(),
    createdAt: z.string(),
    /** Open complaints and their reasons, most frequent first. */
    reports: count,
    reportReasons: z.array(z.string()),
});
// ---------------------------------------------------------------------------
// One user, everything — /admin/users/:id
// ---------------------------------------------------------------------------
export const adminUserDetailSchema = z.object({
    user: adminUserSummarySchema.extend({
        /** What to type on the login form: the account's email. For a Telegram
         * account this is the placeholder address it was created with. */
        loginEmail: z.string(),
        bannerUrl: z.string().nullable(),
        bio: z.string().nullable(),
        onlineStatus: z.string(),
        emailVerified: z.boolean(),
        proSince: z.string().nullable(),
        /** Only whether it is set and the resulting age gate — never the date. */
        ageVerified: z.boolean(),
        referrer: z.string().nullable(),
        achievements: count,
    }),
    activity: z.object({
        pageviews: count,
        pageviews30d: count,
        activeDays30d: count,
        /** One entry per UTC day, oldest first, always 30 of them. */
        daily: z.array(z.object({ date: z.string(), pageviews: count, watchMinutes: count })),
        recentPages: z.array(z.object({ path: z.string(), createdAt: z.string() })),
    }),
    watch: z.object({
        seconds: count,
        sessions: count,
        topAnime: z.array(z.object({
            animeId: z.number().int(),
            slug: z.string(),
            title: z.string(),
            imageUrl: z.string().nullable(),
            seconds: count,
        })),
    }),
    library: z.object({
        byStatus: z.array(z.object({ status: z.enum(ADMIN_LIBRARY_STATUSES), count })),
        recent: z.array(z.object({
            animeId: z.number().int(),
            slug: z.string(),
            title: z.string(),
            imageUrl: z.string().nullable(),
            status: z.enum(ADMIN_LIBRARY_STATUSES),
            score: z.number().int().nullable(),
            progress: count,
            episodes: z.number().int().nullable(),
            updatedAt: z.string(),
        })),
    }),
    comments: z.object({
        total: count,
        deleted: count,
        likesReceived: count,
        recent: z.array(z.object({
            id: z.string(),
            animeSlug: z.string(),
            animeTitle: z.string(),
            body: z.string(),
            likeCount: count,
            deleted: z.boolean(),
            createdAt: z.string(),
        })),
    }),
});
/** A new password chosen by an admin; same rules as signing up. */
export const adminSetPasswordInputSchema = z.object({ password: passwordSchema });
// ---------------------------------------------------------------------------
// Monitoring — /admin/monitoring, read from Prometheus
// ---------------------------------------------------------------------------
export const ADMIN_MONITORING_RANGES = ["1h", "6h", "24h", "7d"];
export const adminMonitoringQuerySchema = z.object({
    range: z.enum(ADMIN_MONITORING_RANGES).default("24h"),
});
export const ADMIN_MONITORING_PANELS = [
    "requests",
    "latency",
    "errors",
    "pageviews",
    "watch",
    "active",
    "signups",
    "memory",
    "cpu",
];
export const adminMonitoringSchema = z.object({
    /** False when Prometheus could not be reached — the panels are then empty
     *  and `error` says why. */
    available: z.boolean(),
    error: z.string().nullable(),
    range: z.enum(ADMIN_MONITORING_RANGES),
    /** Whether Prometheus itself reports the API target as up. */
    targetUp: z.boolean().nullable(),
    lastScrape: z.string().nullable(),
    panels: z.array(z.object({
        id: z.enum(ADMIN_MONITORING_PANELS),
        series: z.array(z.object({
            /** A stable key the page labels ("registered", "guests", "p95"…). */
            key: z.string(),
            /** [unix seconds, value] */
            points: z.array(z.tuple([z.number(), z.number()])),
        })),
    })),
});
/* ------------------------------------------------------------------------ */
/* Analytics tab: everything about visits, for any period, filtered.        */
/* ------------------------------------------------------------------------ */
export const ANALYTICS_AUDIENCES = ["all", "guests", "users"];
export const ANALYTICS_DEVICES = ["all", "mobile", "tablet", "desktop"];
export const adminAnalyticsQuerySchema = z.object({
    /** ISO date-time; defaults to 30 days back. */
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
    audience: z.enum(ANALYTICS_AUDIENCES).default("all"),
    device: z.enum(ANALYTICS_DEVICES).default("all"),
    /** Only page views whose path contains this. */
    path: z.string().trim().max(200).optional(),
    /** Count crawlers too (off by default). */
    bots: z.coerce.boolean().default(false),
    /** IANA zone for days and hours, e.g. Europe/Moscow. */
    tz: z
        .string()
        .regex(/^[A-Za-z_]+(\/[A-Za-z0-9_+-]+){0,2}$/)
        .default("Europe/Moscow"),
});
/** The breakdown tables come a group at a time — only the one on screen. */
export const ANALYTICS_BREAKDOWN_GROUPS = ["pages", "sources", "tech"];
export const adminBreakdownQuerySchema = adminAnalyticsQuerySchema.extend({
    group: z.enum(ANALYTICS_BREAKDOWN_GROUPS).default("pages"),
});
export const ANALYTICS_VISITOR_SORTS = ["last", "first", "pageviews", "sessions", "time"];
export const adminVisitorsQuerySchema = adminAnalyticsQuerySchema.extend({
    q: z.string().trim().max(100).optional(),
    sort: z.enum(ANALYTICS_VISITOR_SORTS).default("last"),
    dir: z.enum(["asc", "desc"]).default("desc"),
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(10).max(100).default(30),
});
// ---------------------------------------------------------------------------
// Attendance by day: unique people and repeat views kept apart.
// ---------------------------------------------------------------------------
export const adminDailyQuerySchema = z.object({
    days: z.coerce.number().int().min(7).max(366).default(30),
});
export const adminDailyRowSchema = z.object({
    day: z.string(),
    /** Unique browsers that day (each counted once, however many pages). */
    visitors: z.number(),
    /** Of them, seen for the first time. */
    newVisitors: z.number(),
    /** Of them, back again. */
    returning: z.number(),
    /** Of them, signed in / guests. */
    registeredVisitors: z.number(),
    guestVisitors: z.number(),
    /** Every page view. */
    pageviews: z.number(),
    /** Page views beyond each visitor's first — the repeats, in a column of their own. */
    repeatViews: z.number(),
    sessions: z.number(),
    /** Sessions of a single page. */
    bounces: z.number(),
    avgSessionSec: z.number(),
    registrations: z.number(),
    watchMinutes: z.number(),
    /** Still going (today) — counted from the live rows, refreshed every few minutes. */
    live: z.boolean(),
});
//# sourceMappingURL=admin.js.map